import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Button, Header, Modal, Accumulator } from '@adc/ui-core'
import DataTableUtils from '../utils'

const EditDataColumnsMenuItem = {
  key: 'edit-data-columns',
  value: 'edit-data-columns',
  text: 'Edit Data Columns',
}

// Generic column type that supports both legacy and modern column formats
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Column {
  field?: string
  id?: string
  title?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  colProps?: any
  [key: string]: unknown
}

interface OptionItem {
  key: string
  value: string
  text: string
}

interface EditDataColumnsModalProps {
  modalOpen: boolean
  closeModal: () => void
  saveConfig: (selectedOptions: string[]) => void
  selectedColumns: Column[]
  reset: () => void
  allColumns: Column[]
}

const getSelectedOptions = (options: Column[]): string[] => {
  return options.reduce<string[]>((init, column) => {
    if (column.colProps?.isVisible) {
      const key = column.field || column.id || ''
      if (key) init.push(key)
    }
    return init
  }, [])
}

const getInitialState = (
  allColumns: Column[],
  selectedColumns: Column[]
): { selectedOptions: string[]; allOptions: OptionItem[] } => {
  const dataColumns = DataTableUtils.getDataColumns(allColumns)
  const allOptions = dataColumns.map((column: Column) => {
    const key = column.field || column.id || ''
    return { key, value: key, text: column.title || key }
  })

  const selectedOptions = getSelectedOptions(selectedColumns)

  return {
    selectedOptions,
    allOptions,
  }
}

const EditDataColumnsModal: React.FC<EditDataColumnsModalProps> = ({
  modalOpen,
  closeModal,
  saveConfig,
  selectedColumns,
  reset,
  allColumns,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [allOptions, setAllOptions] = useState<OptionItem[]>([])
  const [trigger, setTrigger] = useState(0)

  // Initialize state
  const isInitialized = useRef(false)
  useEffect(() => {
    if (!isInitialized.current) {
      const initialState = getInitialState(allColumns, selectedColumns)
      setSelectedOptions(initialState.selectedOptions)
      setAllOptions(initialState.allOptions)
      isInitialized.current = true
    }
  }, [allColumns, selectedColumns])

  // Handle selectedColumns changes (replaces UNSAFE_componentWillReceiveProps)
  const prevSelectedColumnsRef = useRef<Column[]>(selectedColumns)
  useEffect(() => {
    if (prevSelectedColumnsRef.current !== selectedColumns) {
      const newSelectedOptions = getSelectedOptions(selectedColumns)
      setSelectedOptions(newSelectedOptions)
    }
    prevSelectedColumnsRef.current = selectedColumns
  }, [selectedColumns])

  const updateConfigurableColumns = useCallback((values: string[]) => {
    setSelectedOptions(values)
  }, [])

  const saveConfigurableColumns = useCallback(() => {
    saveConfig(selectedOptions)
  }, [saveConfig, selectedOptions])

  const handleReset = useCallback(() => {
    reset()
    const initialState = getInitialState(allColumns, selectedColumns)
    setSelectedOptions(initialState.selectedOptions)
    setAllOptions(initialState.allOptions)
    setTrigger(prev => prev + 1)
  }, [reset, allColumns, selectedColumns])

  return (
    <Modal
      closeOnDimmerClick={false}
      elmId='modal_edit_column_config'
      size='small'
      open={modalOpen}
      onClose={closeModal}
      closeIcon={false}
      mobileTitleSkin='light'
    >
      <Header
        as='h2'
        content={EditDataColumnsMenuItem.text}
        data-elm-id='header_edit_column_config'
      />
      <Modal.Content>
        <p data-elm-id='content_edit_column_config'>
          Select columns to show in the table.
        </p>
        <Accumulator
          elmId='accumulator'
          initialSelectedValues={selectedOptions}
          allOptions={allOptions}
          onUpdate={updateConfigurableColumns}
          selectedListHeader='Selected Columns'
          unselectedListHeader='Available Columns'
          helpText='Tip: you can drag-and-drop to add or re-order columns.'
          triggerUpdate={trigger}
        />
      </Modal.Content>
      <Modal.Actions>
        {/* RTL */}
        <Button
          role='tertiary'
          size='small'
          elmId='button_reset_edit_column_config'
          onClick={handleReset}
        >
          Reset to Default
        </Button>
        <Button
          size='small'
          elmId='button_cancel_edit_column_config'
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          role='primary'
          size='small'
          elmId='button_save_edit_column_config'
          disabled={selectedOptions.length === 0}
          onClick={saveConfigurableColumns}
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default EditDataColumnsModal
export { EditDataColumnsMenuItem }
export type { EditDataColumnsModalProps, Column, OptionItem }
